/**
 * MessagingInbox — Popover inbox for the ContextBar notification bell.
 *
 * Zulip-style layout grouped by Account (customer), then by entity type within
 * each account. Shows last message preview, timeAgo, and unread badges.
 */

import React, { useState } from "react";
import {
  Popover,
  Badge,
  Button,
  Typography,
  Spin,
  Empty,
  Tag,
  Divider,
  theme,
} from "antd";
import {
  BellOutlined,
  MessageOutlined,
  RightOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useMessageInbox } from "@/hooks/useMessageInbox";
import { ThreadListItem } from "./ThreadListItem";

const { Text, Title } = Typography;

export const MessagingInbox: React.FC = () => {
  const {
    token: { colorBorder, fontSizeSM },
  } = theme.useToken();

  const navigate = useNavigate();
  const { accountGroups, groups, totalUnread, isLoading } = useMessageInbox();
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(
    new Set()
  );

  const toggleAccount = (accountId: string) => {
    setCollapsedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  // Filter by module type if active
  const filteredAccountGroups = accountGroups
    .map((ag) => {
      if (!activeFilter) return ag;
      const filtered = ag.moduleGroups.filter(
        (mg) => mg.moduleName === activeFilter
      );
      if (filtered.length === 0) return null;
      return {
        ...ag,
        moduleGroups: filtered,
        totalUnread: filtered.reduce((s, mg) => s + mg.totalUnread, 0),
      };
    })
    .filter(Boolean) as typeof accountGroups;

  // Module filter pills
  const moduleFilters = groups.map((g) => ({
    name: g.moduleName,
    label: g.label,
    unread: g.totalUnread,
  }));

  const handleThreadClick = (entityId: string) => {
    setOpen(false);
    navigate(`/messages/${entityId}`);
  };

  const content = (
    <div
      style={{
        width: 400,
        maxHeight: 520,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          <MessageOutlined style={{ marginRight: 8 }} />
          Messaggi
        </Title>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setOpen(false);
            navigate("/messages");
          }}
        >
          Vedi tutti <RightOutlined />
        </Button>
      </div>

      {/* Filter pills */}
      {moduleFilters.length > 1 && (
        <div
          style={{
            padding: "0 16px 8px",
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          <Tag
            color={activeFilter === null ? "blue" : undefined}
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => setActiveFilter(null)}
          >
            Tutti
          </Tag>
          {moduleFilters.map((mf) => (
            <Tag
              key={mf.name}
              color={activeFilter === mf.name ? "blue" : undefined}
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() =>
                setActiveFilter(activeFilter === mf.name ? null : mf.name)
              }
            >
              {mf.label}
              {mf.unread > 0 && (
                <Badge
                  count={mf.unread}
                  size="small"
                  style={{
                    backgroundColor: "#6366f1",
                    marginLeft: 4,
                    boxShadow: "none",
                  }}
                />
              )}
            </Tag>
          ))}
        </div>
      )}

      <Divider style={{ margin: 0 }} />

      {/* Thread list grouped by Account */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : filteredAccountGroups.length === 0 ? (
          <Empty
            description="Nessun messaggio"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: "32px 0" }}
          />
        ) : (
          filteredAccountGroups.map((acctGroup) => {
            const key = acctGroup.accountId || "_none";
            const collapsed = collapsedAccounts.has(key);
            return (
              <div key={key}>
                {/* Account header */}
                <div
                  onClick={() => toggleAccount(key)}
                  style={{
                    padding: "8px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    background: "rgba(99, 102, 241, 0.04)",
                    borderBottom: `1px solid ${colorBorder}`,
                  }}
                >
                  <Text
                    strong
                    style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <BankOutlined style={{ color: "#6366f1" }} />
                    {collapsed ? "▸" : "▾"} {acctGroup.accountName}
                  </Text>
                  {acctGroup.totalUnread > 0 && (
                    <Badge
                      count={acctGroup.totalUnread}
                      size="small"
                      style={{ backgroundColor: "#6366f1" }}
                    />
                  )}
                </div>

                {/* Module subgroups within account */}
                {!collapsed &&
                  acctGroup.moduleGroups.map((modGroup) => (
                    <div key={modGroup.moduleName}>
                      {/* Module subheader (only if multiple modules) */}
                      {acctGroup.moduleGroups.length > 1 && (
                        <div
                          style={{
                            padding: "4px 16px 4px 32px",
                            background: "rgba(0,0,0,0.015)",
                            borderBottom: `1px solid ${colorBorder}`,
                          }}
                        >
                          <Text
                            type="secondary"
                            style={{
                              fontSize: fontSizeSM - 1,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {modGroup.label}
                          </Text>
                        </div>
                      )}
                      {modGroup.threads.map((thread) => (
                        <ThreadListItem
                          key={thread.entityId}
                          thread={thread}
                          onClick={() => handleThreadClick(thread.entityId)}
                        />
                      ))}
                    </div>
                  ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      overlayInnerStyle={{ padding: 0 }}
    >
      <Badge count={totalUnread} size="small">
        <Button type="text" icon={<BellOutlined />} />
      </Badge>
    </Popover>
  );
};
